import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { Plural, Trans } from '@lingui/react/macro';
import { plural, t } from '@lingui/core/macro';
import {
  AlertTriangle,
  CalendarCheck,
  CheckCircle2,
  ChevronDown,
  FileArchive,
  FilePlus2,
  XOctagon,
} from 'lucide-react';
import { djs } from '@duke-hq/djs';
import type { NatFileType, AvetmissResponse } from '@duke-hq/avetmiss/NATTypes';
import {
  transformValues,
  generateFileContent,
  getSortedRulesByPosition,
} from '@duke-hq/avetmiss/NATHelpers';
import { createNAT00080HeaderToFieldMapping } from '@duke-hq/avetmiss/formatters/NAT00080';
import { useCrossFileValidation } from './hooks/useCrossFileValidation';
import { NATCacheProvider } from './contexts/NATCacheProvider';
import type { NATReportStatus, NATReportTiming } from './contexts/NATCacheContext';
import { NATComponent } from './NATComponent';
import { withPermission } from '../withPermission';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { LiveElapsed, formatDuration } from './timing';
import { downloadFile } from '../lib/download';

const getDefaultQuarter = (): { quarter: string; year: string } => {
  const previousQuarter = djs().subtract(1, 'quarter');
  return {
    quarter: `Q${previousQuarter.quarter()}`,
    year: previousQuarter.year().toString(),
  };
};

const quarterMap: Record<string, number> = {
  Q1: 1,
  Q2: 2,
  Q3: 3,
  Q4: 4,
  'All Year': 0,
};

const quarters = ['Q1', 'Q2', 'Q3', 'Q4', 'All Year'];

const AvetmissLayout = React.memo(() => {
  const defaultQuarter = useMemo(() => getDefaultQuarter(), []);
  const params = useParams<{ '*'?: string }>();
  const natFileIdParam = params['*'] != null && params['*'].length > 0 ? params['*'] : undefined;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get('filter') ?? undefined;
  const [activeTab, setActiveTab] = useState(0);
  const [activeQuarter, setActiveQuarter] = useState<string | null>(defaultQuarter.quarter);
  const [selectedYear, setSelectedYear] = useState(defaultQuarter.year);
  const [natResponseCache, setNatResponseCache] = useState<Map<string, AvetmissResponse>>(
    () => new Map(),
  );
  const [statusMap, setStatusMap] = useState<Map<string, NATReportStatus>>(() => new Map());
  const [timingMap, setTimingMap] = useState<Map<string, NATReportTiming>>(() => new Map());
  const [bulkProgress, setBulkProgress] = useState<{
    current: number;
    total: number;
    startedAt: number;
    completedAt: number | null;
  } | null>(null);
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const getCacheKey = useCallback((natFileId: NatFileType, from: string, to: string) => {
    return `${natFileId}-${from}-${to}`;
  }, []);

  const getDateRange = useCallback((year: string, quarter: number) => {
    if (quarter === 0) {
      // All Year
      const startDate = djs(`${year}-01-01`);
      const endDate = djs(`${year}-12-31`);
      return { startDate, endDate };
    }

    const startDate = djs().startOf('year').year(Number(year));
    const endDate = djs().quarter(quarter).year(Number(year)).endOf('quarter');

    return { startDate, endDate };
  }, []);

  const maxYear = new Date().getFullYear();
  const minYear = maxYear - 1;

  const years = useMemo(() => {
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  }, [minYear, maxYear]);

  const { fromDate, toDate } = useMemo(() => {
    const year = selectedYear;
    const quarter = quarterMap[activeQuarter ?? ''];

    if (quarter === undefined) {
      return { fromDate: '', toDate: '' };
    }

    const { startDate, endDate } = getDateRange(year, quarter);
    return {
      fromDate: startDate.format('YYYY-MM-DD'),
      toDate: endDate.format('YYYY-MM-DD'),
    };
  }, [activeQuarter, selectedYear, getDateRange]);

  const tabs = useMemo(() => {
    const tabsData: Array<{
      value: NatFileType;
      label: string;
      title: string;
      hasValidation?: boolean;
    }> = [
      {
        value: 'NAT00010',
        label: t`NAT00010`,
        title: t`Training Organisation File (NAT00010)`,
      },
      {
        value: 'NAT00020',
        label: t`NAT00020`,
        title: t`Training Organisation Delivery Location File (NAT00020)`,
        hasValidation: true,
      },
      {
        value: 'NAT00030',
        label: t`NAT00030`,
        title: t`Program File (NAT00030)`,
        hasValidation: true,
      },
      {
        value: 'NAT00060',
        label: t`NAT00060`,
        title: t`Subject File (NAT00060)`,
        hasValidation: true,
      },
      {
        value: 'NAT00080',
        label: t`NAT00080`,
        title: t`Client File (NAT00080)`,
        hasValidation: true,
      },
      {
        value: 'NAT00085',
        label: t`NAT00085`,
        title: t`Client Contact Details File (NAT00085)`,
      },
      {
        value: 'NAT00090',
        label: t`NAT00090`,
        title: t`Disability File (NAT00090)`,
      },
      {
        value: 'NAT00100',
        label: t`NAT00100`,
        title: t`Prior Educational Achievement File (NAT00100)`,
      },
      {
        value: 'NAT00120',
        label: t`NAT00120`,
        title: t`Training Activity File (NAT00120)`,
        hasValidation: true,
      },
      {
        value: 'NAT00130',
        label: t`NAT00130`,
        title: t`Program Completed File (NAT00130)`,
      },
    ];
    return tabsData;
  }, []);

  useEffect(() => {
    if (natFileIdParam == null) {
      return;
    }
    const index = tabs.findIndex((tab) => tab.value === natFileIdParam);
    if (index >= 0 && index !== activeTab) {
      setActiveTab(index);
    }
  }, [natFileIdParam, tabs, activeTab]);

  const natResponses = useMemo(() => {
    const responses: Partial<Record<NatFileType, AvetmissResponse>> = {};

    tabs.forEach((tab) => {
      const cacheKey = getCacheKey(tab.value, fromDate, toDate);
      const cachedResponse = natResponseCache.get(cacheKey);
      if (cachedResponse) {
        responses[tab.value] = cachedResponse;
      }
    });

    return responses;
  }, [tabs, getCacheKey, fromDate, toDate, natResponseCache]);

  const cacheNatResponse = useCallback(
    (natFileId: NatFileType, response: AvetmissResponse) => {
      const cacheKey = getCacheKey(natFileId, fromDate, toDate);
      setNatResponseCache((prev) => {
        if (prev.has(cacheKey)) {
          return prev;
        }

        const newCache = new Map(prev);
        newCache.set(cacheKey, response);
        return newCache;
      });
    },
    [getCacheKey, fromDate, toDate],
  );

  const getCachedData = useCallback(
    (natFileId: NatFileType, fromDate: string, toDate: string): AvetmissResponse | null => {
      const cacheKey = getCacheKey(natFileId, fromDate, toDate);
      return natResponseCache.get(cacheKey) ?? null;
    },
    [getCacheKey, natResponseCache],
  );

  const getStatus = useCallback(
    (natFileId: NatFileType): NATReportStatus => {
      const cacheKey = getCacheKey(natFileId, fromDate, toDate);
      return statusMap.get(cacheKey) ?? 'idle';
    },
    [getCacheKey, fromDate, toDate, statusMap],
  );

  const getTiming = useCallback(
    (natFileId: NatFileType): NATReportTiming | null => {
      const cacheKey = getCacheKey(natFileId, fromDate, toDate);
      return timingMap.get(cacheKey) ?? null;
    },
    [getCacheKey, fromDate, toDate, timingMap],
  );

  const triggerGeneration = useCallback(
    async (natFileId: NatFileType, options: { force?: boolean } = {}): Promise<void> => {
      if (fromDate.length === 0 || toDate.length === 0) {
        return;
      }

      const cacheKey = getCacheKey(natFileId, fromDate, toDate);

      // Peek current status without depending on statusMap, so triggerGeneration's
      // identity doesn't change every time any report's status changes.
      let currentStatus: NATReportStatus | undefined;
      setStatusMap((prev) => {
        currentStatus = prev.get(cacheKey);
        return prev;
      });
      if (currentStatus === 'loading') {
        return;
      }
      if (currentStatus === 'done' && options.force !== true) {
        return;
      }

      const startedAt = Date.now();
      setStatusMap((prev) => {
        const next = new Map(prev);
        next.set(cacheKey, 'loading');
        return next;
      });
      setTimingMap((prev) => {
        const next = new Map(prev);
        next.set(cacheKey, { startedAt, completedAt: null });
        return next;
      });

      try {
        const response = await utils.avetmiss.get.fetch({ natFileId, fromDate, toDate });
        const completedAt = Date.now();
        setNatResponseCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, response);
          return next;
        });
        setStatusMap((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, 'done');
          return next;
        });
        setTimingMap((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, { startedAt, completedAt });
          return next;
        });
      } catch {
        const completedAt = Date.now();
        setStatusMap((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, 'error');
          return next;
        });
        setTimingMap((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, { startedAt, completedAt });
          return next;
        });
      }
    },
    [fromDate, toDate, getCacheKey, utils.avetmiss.get],
  );

  const updateCachedRowData = useCallback(
    (natFileId: NatFileType, rowIndex: number, updatedData: Record<string, string>) => {
      const cacheKey = getCacheKey(natFileId, fromDate, toDate);

      setNatResponseCache((prev) => {
        const cachedResponse = prev.get(cacheKey);
        if (!cachedResponse) {
          return prev;
        }

        const updatedHeaderNames = Object.keys(updatedData);
        const headerToErrorFieldMap = createNAT00080HeaderToFieldMapping();
        const updatedErrorFields = updatedHeaderNames.map(
          (headerName) => headerToErrorFieldMap[headerName] ?? headerName,
        );

        const updatedResponse: AvetmissResponse = {
          ...cachedResponse,
          data: {
            ...cachedResponse.data,
            rows: cachedResponse.data.rows.map((row, index) => {
              if (index === rowIndex) {
                const updatedRow = [...row];
                cachedResponse.data.headers.forEach((header, colIndex) => {
                  if (updatedData[header] !== undefined) {
                    updatedRow[colIndex] = updatedData[header];
                  }
                });

                return updatedRow;
              }
              return row;
            }),
          },
          errors: cachedResponse.errors.filter((error) => {
            // Keep errors that are not for this row or not for updated fields
            if (error.rowIndex !== rowIndex || !updatedErrorFields.includes(error.field)) {
              return true;
            }

            // For updated fields on this row, check if the new value actually fixes the error
            const headerIndex = cachedResponse.data.headers.findIndex((header) => {
              const mappedField = headerToErrorFieldMap[header] ?? header;
              return mappedField === error.field;
            });

            if (headerIndex !== -1) {
              let newValue: string | undefined = undefined;
              let oldValue: string | undefined = undefined;
              if (headerIndex !== -1) {
                const headerName = cachedResponse.data.headers[headerIndex];
                if (headerName !== undefined) {
                  newValue = updatedData[headerName];
                  oldValue = cachedResponse.data.rows[rowIndex]?.[headerIndex];
                }
              }

              // Only remove the error if the value actually changed
              // If the value is the same, keep the error
              return newValue === oldValue;
            }

            return false; // Remove error if we can't find the header
          }),
        };

        const newCache = new Map(prev);
        newCache.set(cacheKey, updatedResponse);
        return newCache;
      });
    },
    [getCacheKey, fromDate, toDate],
  );

  const allReportsCached = useMemo(() => {
    return tabs.every((tab) => {
      const cacheKey = getCacheKey(tab.value, fromDate, toDate);
      return natResponseCache.has(cacheKey);
    });
  }, [tabs, getCacheKey, fromDate, toDate, natResponseCache]);

  const cachedReportsCount = useMemo(() => {
    return tabs.filter((tab) => {
      const cacheKey = getCacheKey(tab.value, fromDate, toDate);
      return natResponseCache.has(cacheKey);
    }).length;
  }, [tabs, getCacheKey, fromDate, toDate, natResponseCache]);

  const { validationResult, crossFileOnlyReferences } = useCrossFileValidation({
    natResponses,
    enabled: allReportsCached, // Auto-enable validation when all reports are cached
  });

  const periodMessage = useMemo(() => {
    const year = selectedYear;
    const quarter = quarterMap[activeQuarter ?? ''];
    if (quarter === undefined) {
      return '';
    }

    const { startDate, endDate } = getDateRange(year, quarter);
    const formattedStart = startDate.format('MMMM Do, YYYY');
    const formattedEnd = endDate.format('MMMM Do, YYYY');

    return `${formattedStart} - ${formattedEnd}`;
  }, [activeQuarter, selectedYear, getDateRange]);

  const handleYearChange = useCallback((year: string) => {
    setSelectedYear(year);
  }, []);

  const handleQuarterClick = useCallback((quarter: string) => {
    setActiveQuarter(quarter);
  }, []);

  const handleTabChange = useCallback(
    (index: number) => {
      const tab = tabs[index];
      if (tab == null) {
        setActiveTab(0);
        return;
      }
      setActiveTab(index);
      navigate(`/avetmiss/${tab.value}`, { replace: true });
    },
    [tabs, navigate],
  );

  const generateAllReports = useCallback(async () => {
    if (bulkProgress != null && bulkProgress.completedAt == null) {
      return;
    }

    const bulkStartedAt = Date.now();
    setBulkProgress({
      current: 0,
      total: tabs.length,
      startedAt: bulkStartedAt,
      completedAt: null,
    });
    try {
      for (const [i, tab] of tabs.entries()) {
        setBulkProgress({
          current: i,
          total: tabs.length,
          startedAt: bulkStartedAt,
          completedAt: null,
        });
        await triggerGeneration(tab.value);
      }
      setBulkProgress({
        current: tabs.length,
        total: tabs.length,
        startedAt: bulkStartedAt,
        completedAt: Date.now(),
      });
    } catch {
      toast({
        title: t`Bulk generation failed`,
        description: t`One or more reports failed to generate. Check individual tabs for details.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [bulkProgress, tabs, triggerGeneration, toast]);

  const downloadCachedAsZip = useCallback(async () => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    const skippedFiles: NatFileType[] = [];

    const quarter = activeQuarter ?? 'All';
    const downloadStamp = djs().format('YYYYMMDD-HHmmss');
    const zipFileName = `AVETMISS_NAT_Reports_${quarter}_${selectedYear}_${downloadStamp}.zip`;

    for (const tab of tabs) {
      const cacheKey = getCacheKey(tab.value, fromDate, toDate);
      const cachedResponse = natResponseCache.get(cacheKey);

      if (cachedResponse && cachedResponse.data != null) {
        try {
          const sortedRules = getSortedRulesByPosition(tab.value);
          const displayNameToFieldKey = new Map<string, string>();
          sortedRules.forEach(([fieldKey, rule]) => {
            displayNameToFieldKey.set(rule.name, fieldKey);
          });

          const { headers, rows } = cachedResponse.data;
          const rawRows: Array<Record<string, string>> = rows.map((row) => {
            const rowObject: Record<string, string> = {};
            headers.forEach((displayName, index) => {
              const fieldKey = displayNameToFieldKey.get(displayName);
              if (fieldKey != null && fieldKey.length > 0) {
                const cellValue = row[index];
                rowObject[fieldKey] = cellValue != null ? cellValue : '';
              }
            });
            return rowObject;
          });

          const transformedValues = transformValues(tab.value, rawRows);
          const fileContent = generateFileContent(tab.value, transformedValues);

          zip.file(`${tab.value}.txt`, fileContent);
        } catch {
          skippedFiles.push(tab.value);
        }
      }
    }

    if (skippedFiles.length > 0) {
      toast({
        title: t`Some files couldn't be included`,
        description: t`Failed to format: ${skippedFiles.join(', ')}`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }

    try {
      downloadFile(await zip.generateAsync({ type: 'blob' }), zipFileName);
    } catch {
      toast({
        title: t`Failed to generate ZIP file`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [tabs, getCacheKey, fromDate, toDate, natResponseCache, activeQuarter, selectedYear, toast]);

  return (
    <NATCacheProvider
      onCacheData={cacheNatResponse}
      onUpdateRowData={updateCachedRowData}
      onGetCachedData={getCachedData}
      onTriggerGeneration={triggerGeneration}
      onGetStatus={getStatus}
      onGetTiming={getTiming}
    >
      <Box width="full" overflow="auto" height="full">
        <Stack gap="0">
          <Flex flexDirection="column">
            <Box px="4" pt="4">
              <PageHeading>
                <Trans>AVETMISS NAT Files</Trans>
              </PageHeading>
            </Box>

            <Flex px="4" py="3" align="center" gap="4" wrap="wrap" justify="space-between">
              <Flex direction="column" gap="1">
                <Flex alignItems="center" gap="2">
                  <FormControl maxW="fit-content">
                    <Menu>
                      <MenuButton
                        as={Button}
                        variant="outline"
                        size="sm"
                        px={2}
                        width="auto"
                        colorScheme="gray"
                        rightIcon={<ChevronDown size={iconSize.md} />}
                      >
                        {selectedYear || t`Select a year`}
                      </MenuButton>
                      <MenuList>
                        {years.map((year) => (
                          <MenuItem key={year} onClick={() => handleYearChange(year.toString())}>
                            {year}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </FormControl>

                  <ButtonGroup isAttached variant="outline" colorScheme="gray">
                    {quarters.map((quarter) => (
                      <Button
                        key={quarter}
                        size="sm"
                        onClick={() => handleQuarterClick(quarter)}
                        variant={activeQuarter === quarter ? 'solid' : 'outline'}
                        colorScheme={activeQuarter === quarter ? 'gray' : 'gray'}
                      >
                        {quarter === 'All Year' ? (
                          <>
                            <CalendarCheck size={iconSize.md} />
                            <span style={{ marginLeft: '0.25rem' }}>{t`All Year`}</span>
                          </>
                        ) : (
                          quarter
                        )}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Flex>
                <Text fontSize="xs" color="fg.muted">
                  {periodMessage}
                </Text>
              </Flex>

              <Flex direction="column" align={{ base: 'flex-start', md: 'flex-end' }} gap="2">
                {bulkProgress != null && bulkProgress.completedAt == null ? (
                  <Button
                    size="sm"
                    colorScheme="brand"
                    leftIcon={<FileArchive size={iconSize.md} />}
                    isDisabled
                  >
                    <Trans>
                      Generating {bulkProgress.current}/{bulkProgress.total}…
                    </Trans>{' '}
                    <Text as="span" ml={1}>
                      <LiveElapsed startedAt={bulkProgress.startedAt} />
                    </Text>
                  </Button>
                ) : (
                  <Flex gap="2" align="center" wrap="wrap">
                    {cachedReportsCount < tabs.length && (
                      <Button
                        size="sm"
                        colorScheme="brand"
                        leftIcon={<FilePlus2 size={iconSize.md} />}
                        onClick={generateAllReports}
                      >
                        {cachedReportsCount === 0 ? (
                          <Trans>Generate all reports</Trans>
                        ) : (
                          <Trans>Generate {tabs.length - cachedReportsCount} missing reports</Trans>
                        )}
                      </Button>
                    )}
                    {cachedReportsCount > 0 && (
                      <Button
                        size="sm"
                        colorScheme={cachedReportsCount === tabs.length ? 'brand' : 'gray'}
                        variant={cachedReportsCount === tabs.length ? 'solid' : 'outline'}
                        leftIcon={<FileArchive size={iconSize.md} />}
                        onClick={downloadCachedAsZip}
                      >
                        {cachedReportsCount === tabs.length ? (
                          <Trans>Download all as ZIP</Trans>
                        ) : (
                          <Trans>Download {cachedReportsCount} as ZIP</Trans>
                        )}
                      </Button>
                    )}
                  </Flex>
                )}
                <Text fontSize="xs" color="fg.muted">
                  <Trans>
                    {cachedReportsCount} of {tabs.length} cached
                  </Trans>
                  {bulkProgress != null && bulkProgress.completedAt != null && (
                    <Text as="span" ml={2}>
                      <Trans>
                        · Generated in{' '}
                        {formatDuration(bulkProgress.completedAt - bulkProgress.startedAt)}
                      </Trans>
                    </Text>
                  )}
                </Text>
              </Flex>
            </Flex>

            {(() => {
              if (cachedReportsCount === 0) {
                return null;
              }

              const trueCrossFileRefs = crossFileOnlyReferences;

              type FileBreakdown = {
                fileType: NatFileType;
                inFileErrors: number;
                inFileWarnings: number;
                crossFileErrors: number;
                crossFileWarnings: number;
                total: number;
              };

              const perFile: FileBreakdown[] = [];
              let filesWithIssues = 0;
              let totalErrors = 0;
              let totalWarnings = 0;

              tabs.forEach((tab) => {
                const cached = natResponseCache.get(getCacheKey(tab.value, fromDate, toDate));
                if (cached == null) {
                  return;
                }
                const inFileErrors = cached.errors.filter((e) => e.severity === 'error').length;
                const inFileWarnings = cached.errors.filter((e) => e.severity === 'warning').length;
                const refsForFile = trueCrossFileRefs.filter(
                  (ref) => ref.sourceFile === tab.value || ref.targetFile === tab.value,
                );
                const crossFileErrors = refsForFile.filter(
                  (ref) => ref.severity === 'error',
                ).length;
                const crossFileWarnings = refsForFile.filter(
                  (ref) => ref.severity === 'warning',
                ).length;
                const fileTotal =
                  inFileErrors + inFileWarnings + crossFileErrors + crossFileWarnings;
                if (fileTotal > 0) {
                  filesWithIssues += 1;
                }
                totalErrors += inFileErrors + crossFileErrors;
                totalWarnings += inFileWarnings + crossFileWarnings;
                perFile.push({
                  fileType: tab.value,
                  inFileErrors,
                  inFileWarnings,
                  crossFileErrors,
                  crossFileWarnings,
                  total: fileTotal,
                });
              });

              const hasIssues = filesWithIssues > 0;

              if (!hasIssues) {
                return (
                  <Box px="4" pb="3">
                    <Flex
                      align="center"
                      gap={2}
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="green.subtle"
                      borderWidth="1px"
                      borderColor="green.muted"
                    >
                      <Icon as={CheckCircle2} boxSize={iconSize.sm} color="green.600" />
                      <Text fontSize="sm">
                        <Trans>No issues found across {cachedReportsCount} generated files</Trans>
                      </Text>
                    </Flex>
                  </Box>
                );
              }

              const filesWithAnyIssues = perFile.filter((f) => f.total > 0);

              return (
                <Box px="4" pb="3" id="cross-file-validation-summary">
                  <Box
                    borderWidth="1px"
                    borderColor="red.muted"
                    borderRadius="md"
                    bg="red.subtle"
                    p={3}
                  >
                    {(() => {
                      const errorPart = plural(totalErrors, {
                        one: '# error',
                        other: '# errors',
                      });
                      const warningPart =
                        totalWarnings > 0
                          ? `, ${plural(totalWarnings, { one: '# warning', other: '# warnings' })}`
                          : '';
                      return (
                        <Flex align="center" gap={2} mb={3}>
                          <Icon as={AlertTriangle} boxSize={iconSize.sm} color="red.600" />
                          <Text fontSize="sm" fontWeight="semibold">
                            <Trans>
                              {filesWithIssues} of {cachedReportsCount} generated files have issues
                              · {errorPart}
                              {warningPart}
                            </Trans>
                          </Text>
                        </Flex>
                      );
                    })()}
                    <Stack gap={1.5}>
                      {filesWithAnyIssues.map((file) => {
                        const chips: {
                          label: string;
                          filter: string;
                          severity: 'error' | 'warning';
                        }[] = [];
                        if (file.inFileErrors > 0) {
                          chips.push({
                            label: plural(file.inFileErrors, {
                              one: '# in-file error',
                              other: '# in-file errors',
                            }),
                            filter: 'errors',
                            severity: 'error',
                          });
                        }
                        if (file.crossFileErrors > 0) {
                          chips.push({
                            label: plural(file.crossFileErrors, {
                              one: '# cross-file error',
                              other: '# cross-file errors',
                            }),
                            filter: 'crossFileErrors',
                            severity: 'error',
                          });
                        }
                        if (file.inFileWarnings > 0) {
                          chips.push({
                            label: plural(file.inFileWarnings, {
                              one: '# in-file warning',
                              other: '# in-file warnings',
                            }),
                            filter: 'warnings',
                            severity: 'warning',
                          });
                        }
                        if (file.crossFileWarnings > 0) {
                          chips.push({
                            label: plural(file.crossFileWarnings, {
                              one: '# cross-file warning',
                              other: '# cross-file warnings',
                            }),
                            filter: 'crossFileWarnings',
                            severity: 'warning',
                          });
                        }
                        return (
                          <Flex
                            key={file.fileType}
                            align="center"
                            gap={3}
                            px={2}
                            py={1.5}
                            borderRadius="sm"
                            bg="bg.surface"
                            wrap="wrap"
                          >
                            <Text fontSize="sm" fontWeight="medium" minW={pxToRem(80)}>
                              {file.fileType}
                            </Text>
                            <ButtonGroup
                              size="xs"
                              isAttached
                              variant="outline"
                              colorScheme="gray"
                              flex="1"
                            >
                              {chips.map((chip) => {
                                const isActive =
                                  file.fileType === natFileIdParam && chip.filter === filterParam;
                                const iconColor =
                                  chip.severity === 'error' ? 'red.500' : 'yellow.500';
                                return (
                                  <Button
                                    key={chip.filter}
                                    variant={isActive ? 'solid' : 'outline'}
                                    aria-pressed={isActive}
                                    leftIcon={
                                      <Icon
                                        as={chip.severity === 'error' ? XOctagon : AlertTriangle}
                                        boxSize={iconSize.sm}
                                        color={iconColor}
                                      />
                                    }
                                    onClick={() => {
                                      navigate(`/avetmiss/${file.fileType}?filter=${chip.filter}`, {
                                        replace: true,
                                      });
                                    }}
                                  >
                                    {chip.label}
                                  </Button>
                                );
                              })}
                            </ButtonGroup>
                          </Flex>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>
              );
            })()}
          </Flex>

          <Tabs index={activeTab} onChange={handleTabChange}>
            <TabList bg="bg.subtle">
              {tabs.map((tab) => {
                const cachedResponse = natResponseCache.get(
                  getCacheKey(tab.value, fromDate, toDate),
                );
                const fileErrorCount =
                  cachedResponse?.errors.filter((e) => e.severity === 'error').length ?? 0;
                const fileWarningCount =
                  cachedResponse?.errors.filter((e) => e.severity === 'warning').length ?? 0;
                const tabCrossFileRefs = crossFileOnlyReferences.filter(
                  (ref) => ref.sourceFile === tab.value || ref.targetFile === tab.value,
                );
                const crossFileErrorCount = tabCrossFileRefs.filter(
                  (ref) => ref.severity === 'error',
                ).length;
                const crossFileWarningCount = tabCrossFileRefs.filter(
                  (ref) => ref.severity === 'warning',
                ).length;
                const hasAnyErrors = fileErrorCount > 0 || crossFileErrorCount > 0;
                const hasAnyWarnings = fileWarningCount > 0 || crossFileWarningCount > 0;

                const tabStatus = getStatus(tab.value);
                const tabTiming = getTiming(tab.value);

                let tooltip: React.ReactNode = null;
                if (tabStatus === 'loading' && tabTiming != null) {
                  tooltip = (
                    <Trans>
                      Generating… <LiveElapsed startedAt={tabTiming.startedAt} />
                    </Trans>
                  );
                } else if (tabStatus === 'done') {
                  const totalIssues =
                    fileErrorCount + fileWarningCount + crossFileErrorCount + crossFileWarningCount;
                  if (totalIssues > 0) {
                    tooltip = <Plural value={totalIssues} one="# issue" other="# issues" />;
                  } else {
                    tooltip = <Trans>No issues</Trans>;
                  }
                } else if (tabStatus === 'error') {
                  tooltip = <Trans>Failed. Click to retry.</Trans>;
                } else {
                  tooltip = <Trans>Click to generate this report</Trans>;
                }

                let indicatorColor: string | undefined;
                if (tabStatus === 'done') {
                  if (hasAnyErrors) {
                    indicatorColor = 'red.500';
                  } else if (hasAnyWarnings) {
                    indicatorColor = 'orange.400';
                  } else {
                    indicatorColor = 'green.500';
                  }
                }

                let indicatorLabel = '';
                if (hasAnyErrors) {
                  indicatorLabel = t`Has errors`;
                } else if (hasAnyWarnings) {
                  indicatorLabel = t`Has warnings`;
                } else if (tabStatus === 'done') {
                  indicatorLabel = t`No issues`;
                }

                return (
                  <Tooltip key={tab.value} label={tooltip} openDelay={300} hasArrow>
                    <Tab
                      position="relative"
                      flexShrink={0}
                      whiteSpace="nowrap"
                      color={tabStatus === 'error' ? 'red.600' : undefined}
                    >
                      <Flex align="center" gap={2}>
                        {tabStatus === 'idle' && (
                          <Icon
                            as={FilePlus2}
                            boxSize={iconSize.md}
                            color="fg.muted"
                            aria-label={t`Not yet generated`}
                          />
                        )}
                        {tab.label}
                        {tabStatus === 'loading' && <Spinner size="xs" />}
                        {indicatorColor != null && (
                          <Box
                            boxSize={2}
                            borderRadius="full"
                            bg={indicatorColor}
                            aria-label={indicatorLabel}
                          />
                        )}
                      </Flex>
                    </Tab>
                  </Tooltip>
                );
              })}
            </TabList>

            <TabPanels width="100%" height="100%">
              {tabs.map((tab, index) => {
                const trueCrossFileRefs = crossFileOnlyReferences.filter(
                  (ref) => ref.sourceFile === tab.value || ref.targetFile === tab.value,
                );
                const trueCrossFileErrorCount = trueCrossFileRefs.filter(
                  (ref) => ref.severity === 'error',
                ).length;
                const trueCrossFileWarningCount = trueCrossFileRefs.filter(
                  (ref) => ref.severity === 'warning',
                ).length;
                const crossFileErrorSourceRowIndices = Array.from(
                  new Set(
                    trueCrossFileRefs
                      .filter(
                        (ref) =>
                          ref.severity === 'error' &&
                          ref.sourceFile === tab.value &&
                          ref.sourceRowIndex >= 0,
                      )
                      .map((ref) => ref.sourceRowIndex),
                  ),
                );
                const crossFileWarningSourceRowIndices = Array.from(
                  new Set(
                    trueCrossFileRefs
                      .filter(
                        (ref) =>
                          ref.severity === 'warning' &&
                          ref.sourceFile === tab.value &&
                          ref.sourceRowIndex >= 0,
                      )
                      .map((ref) => ref.sourceRowIndex),
                  ),
                );

                const validationReferences = trueCrossFileRefs
                  .filter((ref) => ref.sourceFile === tab.value && ref.sourceRowIndex >= 0)
                  .map((ref) => ({
                    rowIndex: ref.sourceRowIndex,
                    rule: ref.rule,
                    missingKey: ref.missingKey,
                    severity: ref.severity,
                    targetFile: ref.targetFile,
                  }));

                const natComponentProps = {
                  natFileId: tab.value,
                  title: tab.title,
                  fromDate: fromDate,
                  toDate: toDate,
                  validationAvailable: validationResult != null,
                  validationErrorCount: trueCrossFileErrorCount,
                  validationWarningCount: trueCrossFileWarningCount,
                  validationErrorSourceRowIndices: crossFileErrorSourceRowIndices,
                  validationWarningSourceRowIndices: crossFileWarningSourceRowIndices,
                  validationReferences,
                  initialFilter: activeTab === index ? filterParam : undefined,
                };

                return (
                  <TabPanel key={`tab-${tab.value.toLowerCase()}`} width="100%" height="100%" p={0}>
                    {activeTab === index && <NATComponent {...natComponentProps} />}
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        </Stack>
      </Box>
    </NATCacheProvider>
  );
});

AvetmissLayout.displayName = 'AvetmissPage';
export const AvetmissPage = withPermission('canViewAvetmiss', AvetmissLayout);
